/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import { Stage, Layer } from "react-konva";
import { useNavigate } from "react-router-dom";
import DraggableImage from "./components/DraggableImage";
import { Controls } from "./components/Controls";
import "./SwingieEditYourCharacter.css";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { showAlert } from "../../../../generalUtils";
import { useLoadImages } from "./hooks/useLoadImages";
import { useImageHandlers } from "./hooks/useImageHandlers";
import { useAppContext } from "../../../../AppContext";
import { handleSaveAndFinish } from "./SaveAndLoad";
import moveTheHeadGif from "../../../../assets/gifs/move_the_heads_432_milk.gif";
import { SERVER_URL } from "../../../../config/serverUrl";
import FlowCountdown from "../../shared/FlowCountdown";

const SwingieEditYourCharacterPage = () => {
  const serverUrl = SERVER_URL;
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const { eventName } = useAppContext();
  const { showLoading, hideLoading } = useLoading();

  const [activeImage, setActiveImage] = useState(null);
  const [showGif, setShowGif] = useState(false);
  const containerRef = useRef(null);
  const pinchRef = useRef(null); // track two-finger pinch gesture state
  const dragRef = useRef(null); // track single-finger/mouse drag from anywhere
  const imagesRef = useRef([]);
  const activeImageRef = useRef(null);

  // Load images using a custom hook
  const { images, setImages, canvasWidth, isLoadingComplete } = useLoadImages(
    serverUrl,
    stageRef,
    showLoading,
    hideLoading,
    navigate,
    showAlert
  );

  // keep latest values for non-React touch listeners
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(() => {
    activeImageRef.current = activeImage;
  }, [activeImage]);

  // Extract selected faces and bodies from images state
  const selectedFaces = images
    .filter((img) => img.draggable)
    .map((img) => img.originalSrc);
  const selectedBodies = images
    .filter((img) => !img.draggable)
    .map((img) => img.originalSrc);

  // Image manipulation handlers
  const { handleResize, handleFlip, handleUpdatePosition, handleZIndexChange } =
    useImageHandlers(
      activeImage,
      setActiveImage,
      stageRef,
      images,
      setImages,
      selectedFaces,
      selectedBodies,
      serverUrl
    );

  // Check if all draggable heads have been moved
  const allHeadsMoved = images
    .filter((img) => img.draggable)
    .every((img) => img.hasMoved);

  const anyHeadMoved = images
    .filter((img) => img.draggable)
    .some((img) => img.hasMoved);

  useEffect(() => {
    if (!anyHeadMoved) {
      const timer = setTimeout(() => {
        setShowGif(true);
      }, 6000);
      return () => clearTimeout(timer);
    } else {
      setShowGif(false);
    }
  }, [anyHeadMoved]);

  // Footer button actions
  const footerButtons = async (name) => {
    showLoading();
    const encodedEventName = encodeURIComponent(eventName);
    if (name === "back") {
      navigate(`/${encodedEventName}/body-gallery`);
    } else if (name === "finish") {
      await handleSaveAndFinish(stageRef, setActiveImage, navigate, eventName);
    }
  };

  const isSelectedImageBody = () => {
    if (!activeImage) return false;
    const selectedImage = images.find((img) => img.src === activeImage);
    return selectedImage && !selectedImage.draggable;
  };

  // helpers for global interactions
  const getStage = () => stageRef.current || null;
  const getStageRect = () => {
    const st = getStage();
    if (!st) return null;
    const c = st.container();
    if (!c) return null;
    return c.getBoundingClientRect();
  };
  const isInInteractive = (el) => {
    if (!el) return false;
    const selectors = [
      ".swingie-edityourcharacter-controls-container",
      ".swingie-edityourcharacter-footer",
    ];
    return selectors.some((sel) => el.closest(sel));
  };
  const isPointInStage = (x, y) => {
    const r = getStageRect();
    if (!r) return false;
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };
  const clampPos = (pos, img) => {
    const st = getStage();
    if (!st) return pos;
    const cw = st.width();
    const ch = st.height();
    const buffer = cw * 0.025;
    const newX = Math.max(-buffer, Math.min(pos.x, cw - img.width + buffer));
    const newY = Math.max(0.5, Math.min(pos.y, ch - img.height + buffer));
    return { x: newX, y: newY };
  };
  const moveDrag = (clientX, clientY) => {
    const drag = dragRef.current;
    if (!drag || !drag.active) return;
    const selected = imagesRef.current.find((img) => img.src === drag.src);
    if (!selected) return;
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    const next = clampPos(
      { x: drag.startPos.x + dx, y: drag.startPos.y + dy },
      selected
    );
    // mark moved if distance > 3px
    if (!drag.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) drag.moved = true;
    // update via existing handler to keep state consistent
    handleUpdatePosition(selected.src, next, true);
  };

  // Two-finger pinch anywhere in page container resizes the selected head
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getDistance = (touches) => {
      if (!touches || touches.length < 2) return 0;
      const [t1, t2] = [touches[0], touches[1]];
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e) => {
      const t = e.touches;
      if (!t || t.length === 0) return;
      if (isInInteractive(e.target)) return; // don't interfere with controls/footer
      if (t.length === 2) {
        const activeSrc = activeImageRef.current;
        if (!activeSrc) return;
        const selected = imagesRef.current.find((img) => img.src === activeSrc);
        if (!selected || !selected.draggable) return;
        pinchRef.current = {
          active: true,
          startDistance: getDistance(t),
          startWidth: selected.width,
          src: selected.src,
        };
        e.preventDefault();
        return;
      }
      if (t.length === 1) {
        // start a global drag only if outside the stage; otherwise stage handlers will manage
        const cx = t[0].clientX;
        const cy = t[0].clientY;
        if (!isPointInStage(cx, cy) && activeImageRef.current) {
          // prepare for drag or tap-to-deselect on end
          const selected = imagesRef.current.find(
            (img) => img.src === activeImageRef.current
          );
          if (selected && selected.draggable) {
            dragRef.current = {
              active: true,
              src: selected.src,
              startX: cx,
              startY: cy,
              startPos: { ...selected.position },
              moved: false,
              source: "container",
            };
            e.preventDefault();
          }
        }
      }
    };

    const onTouchMove = (e) => {
      const t = e.touches;
      if (!t) return;
      if (pinchRef.current && t.length === 2) {
        const pinch = pinchRef.current;
        const selected = imagesRef.current.find((img) => img.src === pinch?.src);
        if (pinch && pinch.active && selected) {
          const startDist = Math.max(pinch.startDistance || 0.0001, 0.0001);
          const scale = getDistance(t) / startDist;
          const targetWidth = pinch.startWidth * scale;
          const delta = targetWidth - selected.width;
          if (Math.abs(delta) > 0.15) {
            handleResize(delta);
          }
          e.preventDefault();
          return;
        }
      }
      if (dragRef.current && t.length === 1) {
        moveDrag(t[0].clientX, t[0].clientY);
        e.preventDefault();
      }
    };

    const onTouchEnd = (e) => {
      const t = e.touches;
      if (!t || t.length < 2) pinchRef.current = null;
      const changed = e.changedTouches && e.changedTouches[0];
      if (dragRef.current) {
        const cx = changed ? changed.clientX : 0;
        const cy = changed ? changed.clientY : 0;
        // tap outside stage should clear selection
        if (!dragRef.current.moved && dragRef.current.source === "container") {
          if (!isPointInStage(cx, cy)) setActiveImage(null);
        }
        dragRef.current = null;
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [handleResize]);

  // Stage-level: empty area tap deselects; drag from empty area moves selected head
  const handleStageTouchStart = (e) => {
    const stage = e.target.getStage();
    const isEmpty = e.target === stage;
    const isLayer = e.target && e.target.getClassName && e.target.getClassName() === "Layer";
    const targetSrc = e.target && e.target.attrs && e.target.attrs.image && e.target.attrs.image.src;
    const targetImg = targetSrc && imagesRef.current.find((img) => img.src === targetSrc);
    const isBodyTarget = !!(targetImg && !targetImg.draggable);
    const touches = e.evt && e.evt.touches;
    if (!touches) return;
    if (touches.length === 1 && (isEmpty || isLayer || isBodyTarget)) {
      const cx = touches[0].clientX;
      const cy = touches[0].clientY;
      if (activeImageRef.current) {
        const selected = imagesRef.current.find(
          (img) => img.src === activeImageRef.current
        );
        if (selected && selected.draggable) {
          dragRef.current = {
            active: true,
            src: selected.src,
            startX: cx,
            startY: cy,
            startPos: { ...selected.position },
            moved: false,
            source: "stage",
          };
          e.evt.preventDefault?.();
        }
      } else {
        setActiveImage(null);
      }
    }
  };
  const handleStageTouchMove = (e) => {
    const touches = e.evt && e.evt.touches;
    if (touches && touches.length === 1 && dragRef.current && dragRef.current.source === "stage") {
      moveDrag(touches[0].clientX, touches[0].clientY);
      e.evt.preventDefault?.();
    }
  };
  const handleStageTouchEnd = (e) => {
    if (dragRef.current && dragRef.current.source === "stage") {
      if (!dragRef.current.moved) setActiveImage(null);
      dragRef.current = null;
    }
  };
  const handleStageMouseDown = (e) => {
    const stage = e.target.getStage();
    const isEmpty = e.target === stage;
    const isLayer = e.target && e.target.getClassName && e.target.getClassName() === "Layer";
    const targetSrc = e.target && e.target.attrs && e.target.attrs.image && e.target.attrs.image.src;
    const targetImg = targetSrc && imagesRef.current.find((img) => img.src === targetSrc);
    const isBodyTarget = !!(targetImg && !targetImg.draggable);
    if (isEmpty || isLayer || isBodyTarget) {
      if (activeImageRef.current) {
        const selected = imagesRef.current.find(
          (img) => img.src === activeImageRef.current
        );
        if (selected && selected.draggable) {
          dragRef.current = {
            active: true,
            src: selected.src,
            startX: e.evt.clientX,
            startY: e.evt.clientY,
            startPos: { ...selected.position },
            moved: false,
            source: "stage",
          };
        }
      } else {
        setActiveImage(null);
      }
    }
  };
  const handleStageMouseMove = (e) => {
    if (dragRef.current && dragRef.current.source === "stage") {
      moveDrag(e.evt.clientX, e.evt.clientY);
    }
  };
  const handleStageMouseUp = (e) => {
    if (dragRef.current && dragRef.current.source === "stage") {
      if (!dragRef.current.moved) setActiveImage(null);
      dragRef.current = null;
    }
  };

  return (
    <div ref={containerRef} className="swingie-edityourcharacter-container">
      <FlowCountdown />
      <header className="swingie-edityourcharacter-header rtl">
        {selectedFaces.length === 1 ? "התאימו את הראש לתוצאה מושלמת" : "התאימו ראשים לתוצאה מושלמת"}
      </header>
      <div className="swingie-edityourcharacter-controls-container">
        <Controls
          handleResize={handleResize}
          handleFlip={handleFlip}
          handleZIndexChange={handleZIndexChange}
          activeImage={activeImage}
          isBodySelected={isSelectedImageBody()}
        />
      </div>
      <div className="swingie-edityourcharacter-canvas-wrapper">
        <div
          className="swingie-edityourcharacter-canvas-container"
          style={{ width: canvasWidth, height: window.innerWidth }}
        >
          <Stage
            ref={stageRef}
            width={canvasWidth}
            height={window.innerWidth}
            onTouchStart={handleStageTouchStart}
            onTouchMove={handleStageTouchMove}
            onTouchEnd={handleStageTouchEnd}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleStageMouseUp}
          >
            <Layer>
              {images.map((image, index) => (
                <DraggableImage
                  key={index}
                  src={image.src}
                  width={image.width}
                  height={image.height}
                  initialPosition={image.position}
                  canvasWidth={canvasWidth + 10}
                  canvasHeight={window.innerWidth}
                  isSelected={image.draggable && activeImage === image.src}
                  onSelect={image.draggable ? () => setActiveImage(image.src) : undefined}
                  onUpdatePosition={(src, newPosition, hasMoved) =>
                    handleUpdatePosition(src, newPosition, hasMoved)
                  }
                  draggable={image.draggable}
                />
              ))}
            </Layer>
          </Stage>
        </div>
      </div>

      {showGif && (
        <div className="swingie-edityourcharacter-gif-container">
          <img
            src={moveTheHeadGif}
            alt="Guide GIF"
            className="swingie-edityourcharacter-guide-gif"
          />
        </div>
      )}

      <footer className="swingie-edityourcharacter-footer">
        <button
          className="swingie-edityourcharacter-footer-button"
          onClick={() => footerButtons("back")}
        >
          חזרה
        </button>
        <button
          className="swingie-edityourcharacter-footer-button"
          onClick={() => footerButtons("finish")}
          disabled={!isLoadingComplete || !allHeadsMoved}
        >
          סיום
        </button>
      </footer>
    </div>
  );
};

export default SwingieEditYourCharacterPage;
